import { ErrorMessage, Field } from "formik";
import { MapPin } from "lucide-react";
import TextError from "../../forms/TextError";
import AsyncPortSelect from "./AsyncPortSelect";
import AsyncTransporterSelect from "./AsyncTransporterSelect";
import PortAutoComplete from "./PortAutoComplete";
import SectionCard from "./SectionCard";
import FieldLabel from "./FieldLabel";
import { OCEAN_HAULAGE_MODE, OCEAN_SERVICE_TYPE } from "../../utils/constant";

const INPUT_CLS =
  "h-[42px] px-3.5 focus:outline-none block w-full text-sm border border-gray-200 rounded-xl text-gray-800 bg-white transition-colors focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 hover:border-gray-300 disabled:bg-gray-50 disabled:text-gray-400 disabled:border-gray-100 disabled:cursor-not-allowed disabled:hover:border-gray-100";

// Origin info section — every field is gated by one of the three
// origin-side Rate scope toggles:
//  - Door Origin       → Door Origin, Pickup Service Type, Pickup
//                        Transporter, Pickup Haulage Mode
//  - Inland CY Origin  → Inland CY Origin (an ocean-port lookup, same as POL)
//  - CY Origin         → POL, Via Port Origin, Origin Depot
// POL / Inland CY Origin filter the preloaded `portList` (list_ocean_port)
// client-side; Door Origin / Via Port Origin are live lookups against
// ocean-manual-quote/get-locations (see AsyncPortSelect).
const OriginInfoForm = ({ formikData, portList, complete, readOnly }) => {
  const { values } = formikData;
  const scope = values.scope || {};
  const doorOriginOn = !!scope.doorOrigin;
  const inlandCyOriginOn = !!scope.inlandCyOrigin;
  const cyOriginOn = !!scope.cyOrigin;

  return (
    <SectionCard
      id="origin-section"
      icon={MapPin}
      title="Origin information"
      subtitle="Pickup, port of loading and inland routing"
      complete={complete}
    >
      <div className="oq-field-grid">
        <div>
          <FieldLabel required={doorOriginOn}>Door Origin</FieldLabel>
          <AsyncPortSelect
            name="originInfo.doorOrigin"
            value={values.originInfo?.doorOrigin}
            setFieldValue={formikData.setFieldValue}
            placeholder={doorOriginOn ? "Door Origin" : "Enable 'Door Origin' in Rate scope"}
            disabled={readOnly || !doorOriginOn}
            locationType="origin"
          />
          <ErrorMessage name="originInfo.doorOrigin" component={TextError} />
        </div>

        <div>
          <FieldLabel>Pickup Service Type</FieldLabel>
          <Field
            as="select"
            name="originInfo.pickupServiceType"
            className={INPUT_CLS}
            disabled={readOnly || !doorOriginOn}
          >
            <option value="">{doorOriginOn ? "Select Service Type" : "Enable 'Door Origin' in Rate scope"}</option>
            {OCEAN_SERVICE_TYPE.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Field>
        </div>

        <div>
          <FieldLabel>Pickup Transporter</FieldLabel>
          <AsyncTransporterSelect
            name="originInfo.pickupTransporter"
            value={values.originInfo?.pickupTransporter}
            setFieldValue={formikData.setFieldValue}
            placeholder={doorOriginOn ? "Pickup Transporter" : "Enable 'Door Origin' in Rate scope"}
            disabled={readOnly || !doorOriginOn}
          />
        </div>

        <div>
          <FieldLabel hint="The mode of transport moving cargo between the door and the port — road, rail or barge.">
            Pickup Haulage Mode
          </FieldLabel>
          <Field
            as="select"
            name="originInfo.pickupHaulageMode"
            className={INPUT_CLS}
            disabled={readOnly || !doorOriginOn}
          >
            <option value="">{doorOriginOn ? "Select Pickup Haulage" : "Enable 'Door Origin' in Rate scope"}</option>
            {OCEAN_HAULAGE_MODE.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Field>
        </div>

        <div>
          <FieldLabel required={cyOriginOn} hint="Port of Loading — where the container boards the vessel. Fixed to the query's own origin, not editable.">POL</FieldLabel>
          <PortAutoComplete
            name="originInfo.pol"
            value={values.originInfo?.pol}
            options={portList}
            setFieldValue={formikData.setFieldValue}
            placeholder={cyOriginOn ? "POL" : "Enable 'CY Origin' in Rate scope"}
            disabled
          />
          <ErrorMessage name="originInfo.pol" component={TextError} />
        </div>

        <div>
          <FieldLabel required={inlandCyOriginOn} hint="Container yard at an inland location before the cargo reaches POL. Fixed to the query's own origin, not editable.">
            Inland CY Origin
          </FieldLabel>
          <PortAutoComplete
            name="originInfo.inlandCyOrigin"
            value={values.originInfo?.inlandCyOrigin}
            options={portList}
            setFieldValue={formikData.setFieldValue}
            placeholder={inlandCyOriginOn ? "Inland CY Origin" : "Enable 'Inland CY Origin' in Rate scope"}
            disabled
          />
          <ErrorMessage name="originInfo.inlandCyOrigin" component={TextError} />
        </div>

        <div>
          <FieldLabel hint="An intermediate port the cargo transships through before reaching POL, if any.">Via Port Origin</FieldLabel>
          <AsyncPortSelect
            name="originInfo.viaPortOrigin"
            value={values.originInfo?.viaPortOrigin}
            setFieldValue={formikData.setFieldValue}
            placeholder={cyOriginOn ? "Via Port Origin" : "Enable 'CY Origin' in Rate scope"}
            disabled={readOnly || !cyOriginOn}
            locationType="origin"
          />
        </div>

        <div>
          <FieldLabel>Origin Depot</FieldLabel>
          <Field
            type="text"
            name="originInfo.originDepot"
            placeholder={cyOriginOn ? "Enter Origin Depot" : "Enable 'CY Origin' in Rate scope"}
            className={INPUT_CLS}
            disabled={readOnly || !cyOriginOn}
          />
        </div>
      </div>
    </SectionCard>
  );
};

export default OriginInfoForm;
